// src/features/merchantConfig/components/editors/DateTimeEditor.jsx
//
// Datetime editor — wraps an <input type="datetime-local"> and translates
// between its naive local representation ("YYYY-MM-DDTHH:mm") and the
// timezone-anchored ISO string the API stores.

import React, { useEffect, useState } from "react";
import { inputClass } from "./editorChrome";
import {
  isoToLocalInputValue,
  localInputValueToIso,
} from "../../lib/typeCoercion";
import { validateIsoDateTime } from "../../lib/fieldValidators";

/**
 * @param {{
 *   value: string,
 *   onCommit: (next: string) => void,
 *   onInvalid?: (msg: string) => void,
 *   ariaLabel: string,
 *   hasError?: boolean,
 *   inputId?: string,
 * }} props
 */
export default function DateTimeEditor({
  value,
  onCommit,
  onInvalid,
  ariaLabel,
  hasError,
  inputId,
}) {
  const [draft, setDraft] = useState(isoToLocalInputValue(value));

  useEffect(() => {
    setDraft(isoToLocalInputValue(value));
  }, [value]);

  const commit = () => {
    if (!draft) {
      // empty local input — leave the stored value unchanged
      return;
    }
    const iso = localInputValueToIso(draft);
    const err = validateIsoDateTime(iso);
    if (err) {
      if (onInvalid) onInvalid(err);
      return;
    }
    if (iso !== value) onCommit(iso);
  };

  return (
    <input
      id={inputId}
      type="datetime-local"
      value={draft}
      aria-label={ariaLabel}
      aria-invalid={hasError || undefined}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          setDraft(isoToLocalInputValue(value));
          e.currentTarget.blur();
        }
      }}
      className={inputClass({ hasError, extra: "font-mono" })}
    />
  );
}
